
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Button, ListboxStyled, ListboxStyledButton, ListboxStyledOption, TailwindSpinner,
} from 'src/components/common';
import { ListboxWrapper } from '@components/common/Forms';
import { ListboxStyledOptions } from '@components/common/Listbox/ListboxOptions';
import { useTranslation } from 'next-i18next';
import {
  CheckCircleIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  IdentificationIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';

type BillingType = 'individual' | 'company';

type BillingSelectorProps = {
  billingData: ApiSchema<'BillingDataRead'>[];
  setBillingType?: (type: BillingType) => void;
  selectedBillingInfo?: ApiSchema<'BillingDataRead'> | undefined;
  selectBillingInfo?: (item: ApiSchema<'BillingDataRead'>) => void | undefined;
  clearSelectedBillingInfo?: () => void;
  showAddNewBillingData?: boolean;
  className?: string;
  viewType?: 'list' | 'column';
  isLoadingBillingType?: boolean;
};

export const BillingDataSelector = ({
  billingData,
  viewType = 'column',
  setBillingType,
  selectedBillingInfo,
  selectBillingInfo,
  clearSelectedBillingInfo,
  showAddNewBillingData,
  isLoadingBillingType,
  className,
}: BillingSelectorProps) => {
  const { t } = useTranslation('common');
  const shouldRenderBillingData = (Array.isArray(billingData) && billingData.length > 0) && !isLoadingBillingType;
  const isAddingNewBillingData = shouldRenderBillingData && !selectedBillingInfo;

  return (
    <div className={`${className} p-6`}>
      <div className="my-2 font-bold">{t('global.label.choose.payment.invoice.type')}</div>

      <div className="flex items-center justify-between gap-3">
        <ListboxWrapper
          fieldName="userType"
          data={[
            { label: t('global.label.choose.payment.invoice.type.company'), value: 'company' },
            { label: t('global.label.choose.payment.invoice.type.person'), value: 'individual' },
          ]}
          defaultValueKey="value"
          labelKey="label"
          renderListbox={({ lsData, selected, setSelected }) => {
            const selectedItem = selected as { value: string; label: string };
            const buttonLabel = selectedItem.label;

            return (
              <ListboxStyled className="relative w-full" by="value" value={selected}>
                <ListboxStyledButton
                  as={Button}
                  styling="none"
                  className="flex w-full items-center justify-between gap-2 rounded border border-input bg-surface px-4 py-2"
                >
                  {buttonLabel}
                  <ChevronDownIcon className="-mr-2 size-5 shrink-0 text-ink/80" />
                </ListboxStyledButton>

                <ListboxStyledOptions
                  className="scrollbar-thin mt-0.5 max-h-40 min-w-full overflow-y-auto py-1"
                  animation="slideDown"
                  position="right"
                >
                  {lsData.map((field) => (
                    <ListboxStyledOption
                      key={field.value}
                      value={field}
                      className="flex cursor-pointer items-center gap-1.5 py-1.5 pl-2.5 pr-3.5 text-sm hover:bg-secondary hover:text-surface"
                      onClick={() => {
                        setSelected(field);
                        setBillingType?.(field.value as 'individual' | 'company');
                      }}
                    >
                      {field.label}
                    </ListboxStyledOption>
                  ))}
                </ListboxStyledOptions>
              </ListboxStyled>
            );
          }}
        />
      </div>

      {isLoadingBillingType && (
      <div className="flex items-center justify-center py-10">
        <TailwindSpinner className="size-10 text-ink-light" />
      </div>
      )}


      {shouldRenderBillingData && viewType === 'column'
        && <div className="mb-1 mt-5 text-xs font-thin italic">{t('global.label.choose.payment.invoice.hint')}</div>}

      {viewType === 'list' && billingData?.length > 1 && (
      <div className="mb-1 mt-5 flex items-center">
        <span className="mr-2 flex w-fit min-w-10 items-center justify-center rounded-full bg-surface p-2 font-bold ">{billingData?.length}</span>
        {t('global.label.saved.adress')}
      </div>
      )}
      <div className={`${viewType === 'list' ? 'grid max-h-102 grid-cols-1 overflow-y-scroll' : 'grid grid-cols-2 gap-2 md:grid-cols-3 '}`}>
        {shouldRenderBillingData
          && billingData.map((item) => {
            const isSelected = JSON.stringify(item) === JSON.stringify(selectedBillingInfo);

            return (
              <div
                key={JSON.stringify(item)}
                role="presentation"
                onClick={() => selectBillingInfo?.(item)}
                className={`
          relative mb-3 cursor-pointer rounded-lg border border-dashed p-4 text-sm
          transition-all duration-200 ease-in-out
          ${isSelected
                  ? 'border-secondary bg-secondary/10 text-secondary ring-2 ring-secondary/50'
                  : 'hover:bg-secondary/5 hover:text-ink'}
        `}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute -right-2 -top-2 flex size-7 items-center justify-center rounded-full border border-secondary bg-surface shadow-sm">
                    <CheckCircleIcon className="size-5 text-secondary" />
                  </div>
                )}

                <div className="space-y-1">
                  {/* Company name */}
                  {item.companyName && (
                    <div className="flex items-center gap-2 font-semibold">
                      <BuildingOffice2Icon className="size-4 text-secondary" />
                      {item.companyName}
                    </div>
                  )}

                  {/* Identifier */}
                  {(item.identifier || item.registrationNumber) && (
                    <div className="flex items-center gap-2 text-xs text-ink-medium">
                      <IdentificationIcon className="size-4 text-ink-light" />
                      {item.identifier}
                      {item.identifier && item.registrationNumber && ' / '}
                      {item.registrationNumber}
                    </div>
                  )}

                  {/* Bank info */}
                  {(item.bankName || item.bankAccount) && (
                    <div className="flex items-center gap-2">
                      <BanknotesIcon className="size-4 text-ink-light" />
                      <span>
                        {item.bankName}
                        {' '}
                        {item.bankAccount && `• ${item.bankAccount}`}
                      </span>
                    </div>
                  )}

                  {/* Address */}
                  {(item.city || item.address) && (
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="size-4 text-ink-light" />
                      <span>
                        {item.city}
                        {' '}
                        {item.address && `• ${item.address}`}
                      </span>
                    </div>
                  )}

                  {/* Phone */}
                  {item.phone?.number && item.phone?.country?.countryCode && (
                    <div className="flex items-center gap-2">
                      <DevicePhoneMobileIcon className="size-4 text-ink-light" />
                      +
                      {item.phone.country.countryCode}
                      {' '}
                      {item.phone.number}
                    </div>
                  )}

                  {/* Email */}
                  {item.mailingEmail && (
                    <div className="flex items-center gap-2">
                      <EnvelopeIcon className="size-4 text-ink-light" />
                      {item.mailingEmail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        {shouldRenderBillingData && showAddNewBillingData && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => clearSelectedBillingInfo?.()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                clearSelectedBillingInfo?.();
              }
            }}
            className={`
              relative mb-3 flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-center text-sm
              transition-all duration-200 ease-in-out
              ${isAddingNewBillingData
                ? 'border-secondary bg-secondary/10 text-secondary ring-2 ring-secondary/50'
                : 'hover:bg-secondary/5 hover:text-ink'}
            `}
          >
            {isAddingNewBillingData && (
              <div className="absolute -right-2 -top-2 flex size-7 items-center justify-center rounded-full border border-secondary bg-surface shadow-sm">
                <CheckCircleIcon className="size-5 text-secondary" />
              </div>
            )}

            <PlusCircleIcon className="size-7 text-secondary" />
            <div className="font-semibold">{t('page.company.billing.info.add.btn')}</div>
          </div>
        )}
      </div>
    </div>
  );
};
